/**
 * ScheduleSessionModal — Modal to create/edit a coaching session.
 */
import { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, TextInput, Modal,
  Pressable, ScrollView, Platform,
} from 'react-native';
import { X, ChevronDown } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { FONT, RADIUS, SPACE } from '@/constants/tokens';
import type { CoachSession } from '@/lib/coachSchedule';

interface Client {
  id: string;
  name: string;
}

interface Props {
  visible: boolean;
  clients: Client[];
  initialDate?: Date;
  editSession?: CoachSession;
  onSave: (data: {
    client_id: string;
    client_name: string;
    session_date: string;
    start_time: string;
    duration_minutes: number;
    notes: string;
  }) => void;
  onCancel: () => void;
}

const DURATION_OPTIONS = [30, 45, 60, 90];

const HOURS = Array.from({ length: 18 }, (_, i) => i + 5); // 5am to 10pm
const MINUTES = [0, 15, 30, 45];

export default function ScheduleSessionModal({ visible, clients, initialDate, editSession, onSave, onCancel }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [date, setDate] = useState('');
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);

  useEffect(() => {
    if (!visible) return;
    if (editSession) {
      setClientId(editSession.client_id || '');
      setClientName(editSession.client_name || '');
      setDate(editSession.session_date);
      const [hStr, mStr] = (editSession.start_time || '09:00').split(':');
      setHour(parseInt(hStr, 10));
      setMinute(parseInt(mStr, 10));
      setDuration(editSession.duration_minutes);
      setNotes(editSession.notes ?? '');
    } else {
      const d = initialDate || new Date();
      setDate(d.toISOString().split('T')[0]);
      setHour(9);
      setMinute(0);
      setDuration(60);
      setNotes('');
      setClientId('');
      setClientName('');
    }
  }, [visible, editSession, initialDate]);

  const handleSave = () => {
    if (!clientId) return;
    onSave({
      client_id: clientId,
      client_name: clientName,
      session_date: date,
      start_time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
      duration_minutes: duration,
      notes,
    });
  };

  const formatTime = (h: number, m: number) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onCancel}>
      <Pressable style={styles.overlay} onPress={onCancel}>
        <Pressable style={[styles.sheet, { backgroundColor: c.surface }]} onPress={(e) => e.stopPropagation()}>
          <ScrollView showsVerticalScrollIndicator={false}>
            {/* Header */}
            <View style={styles.sheetHeader}>
              <Text style={[styles.sheetTitle, { color: c.text }]}>
                {editSession ? 'Edit Session' : 'Schedule Session'}
              </Text>
              <TouchableOpacity onPress={onCancel}>
                <X size={20} color={c.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Client Picker */}
            <Text style={[styles.label, { color: c.textSecondary }]}>Client</Text>
            <TouchableOpacity
              style={[styles.picker, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}
              onPress={() => setShowClientPicker(!showClientPicker)}
            >
              <Text style={[styles.pickerText, { color: clientName ? c.text : c.textTertiary }]}>
                {clientName || 'Select a client...'}
              </Text>
              <ChevronDown size={16} color={c.textTertiary} />
            </TouchableOpacity>
            {showClientPicker && (
              <View style={[styles.dropdown, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}>
                {clients.map((cl) => (
                  <TouchableOpacity
                    key={cl.id}
                    style={[styles.dropdownItem, clientId === cl.id && { backgroundColor: c.accent + '22' }]}
                    onPress={() => {
                      setClientId(cl.id);
                      setClientName(cl.name);
                      setShowClientPicker(false);
                    }}
                  >
                    <Text style={[styles.dropdownText, { color: c.text }]}>{cl.name}</Text>
                  </TouchableOpacity>
                ))}
                {clients.length === 0 && (
                  <Text style={[styles.dropdownText, { color: c.textTertiary, padding: 12 }]}>No clients</Text>
                )}
              </View>
            )}

            {/* Date */}
            <Text style={[styles.label, { color: c.textSecondary }]}>Date</Text>
            <TextInput
              style={[styles.input, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
              value={date}
              onChangeText={setDate}
              placeholder="YYYY-MM-DD"
              placeholderTextColor={c.textTertiary}
            />

            {/* Time Picker */}
            <Text style={[styles.label, { color: c.textSecondary }]}>Time</Text>
            <View style={styles.timeRow}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }}>
                {HOURS.map((h) =>
                  MINUTES.map((m) => {
                    const selected = h === hour && m === minute;
                    return (
                      <TouchableOpacity
                        key={`${h}:${m}`}
                        style={[
                          styles.timeChip,
                          { borderColor: c.border },
                          selected && { backgroundColor: c.accent, borderColor: c.accent },
                        ]}
                        onPress={() => { setHour(h); setMinute(m); }}
                      >
                        <Text style={[styles.timeChipText, { color: selected ? c.accentForeground : c.text }]}>
                          {formatTime(h, m)}
                        </Text>
                      </TouchableOpacity>
                    );
                  }),
                )}
              </ScrollView>
            </View>

            {/* Duration */}
            <Text style={[styles.label, { color: c.textSecondary }]}>Duration</Text>
            <View style={styles.durationRow}>
              {DURATION_OPTIONS.map((d) => (
                <TouchableOpacity
                  key={d}
                  style={[
                    styles.durationChip,
                    { borderColor: c.border },
                    duration === d && { backgroundColor: c.accent, borderColor: c.accent },
                  ]}
                  onPress={() => setDuration(d)}
                >
                  <Text style={[styles.durationText, { color: duration === d ? c.accentForeground : c.text }]}>
                    {d}min
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Notes */}
            <Text style={[styles.label, { color: c.textSecondary }]}>Notes</Text>
            <TextInput
              style={[styles.input, styles.notesInput, { backgroundColor: c.surfaceVariant, color: c.text, borderColor: c.border }]}
              value={notes}
              onChangeText={setNotes}
              placeholder="Session notes (optional)"
              placeholderTextColor={c.textTertiary}
              multiline
            />

            {/* Buttons */}
            <View style={styles.btnRow}>
              <TouchableOpacity style={[styles.btn, { borderColor: c.border, borderWidth: 1 }]} onPress={onCancel}>
                <Text style={[styles.btnText, { color: c.textSecondary }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.btn, { backgroundColor: c.accent, opacity: clientId ? 1 : 0.5 }]}
                onPress={handleSave}
                disabled={!clientId}
              >
                <Text style={[styles.btnText, { color: c.accentForeground }]}>
                  {editSession ? 'Update' : 'Schedule'}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-end',
  },
  sheet: {
    borderTopLeftRadius: RADIUS.lg,
    borderTopRightRadius: RADIUS.lg,
    padding: SPACE['6'],
    maxHeight: '90%',
  },
  sheetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACE['5'],
  },
  sheetTitle: {
    fontFamily: TYPO.heading.bold,
    fontSize: FONT.xl,
  },
  label: {
    fontFamily: TYPO.body.semibold,
    fontSize: FONT.sm,
    marginBottom: SPACE['1'],
    marginTop: SPACE['3'],
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  picker: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderRadius: RADIUS.md,
    padding: SPACE['3'],
  },
  pickerText: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.base,
  },
  dropdown: {
    borderWidth: 1,
    borderRadius: RADIUS.md,
    marginTop: SPACE['1'],
    maxHeight: 180,
  },
  dropdownItem: {
    padding: SPACE['3'],
  },
  dropdownText: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.base,
  },
  input: {
    borderWidth: 1,
    borderRadius: RADIUS.md,
    padding: SPACE['3'],
    fontFamily: TYPO.body.regular,
    fontSize: FONT.base,
  },
  notesInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  timeRow: {
    flexDirection: 'row',
  },
  timeChip: {
    borderWidth: 1,
    borderRadius: RADIUS.md,
    paddingHorizontal: SPACE['3'],
    paddingVertical: SPACE['2'],
    marginRight: SPACE['1.5'],
  },
  timeChipText: {
    fontFamily: TYPO.body.medium,
    fontSize: FONT.xs,
  },
  durationRow: {
    flexDirection: 'row',
    gap: SPACE['2'],
  },
  durationChip: {
    flex: 1,
    borderWidth: 1,
    borderRadius: RADIUS.md,
    paddingVertical: SPACE['2'],
    alignItems: 'center',
  },
  durationText: {
    fontFamily: TYPO.body.semibold,
    fontSize: FONT.sm,
  },
  btnRow: {
    flexDirection: 'row',
    gap: SPACE['3'],
    marginTop: SPACE['6'],
    marginBottom: SPACE['4'],
  },
  btn: {
    flex: 1,
    borderRadius: RADIUS.md,
    paddingVertical: SPACE['3'],
    alignItems: 'center',
  },
  btnText: {
    fontFamily: TYPO.body.bold,
    fontSize: FONT.base,
  },
});
